import { X, Sparkles } from "lucide-react";

const PANEL_WIDTH = 288;

export default function LeftPanel({ id, label, onClose, onAddElement }) {
  return (
    <aside
      data-testid={`md-panel-${id}`}
      className="shrink-0 border-r border-[var(--pl-border)] bg-[var(--pl-surface)]/85 backdrop-blur flex flex-col"
      style={{ width: PANEL_WIDTH }}
    >
      <div className="h-12 px-3 flex items-center justify-between border-b border-[var(--pl-border)]">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] font-semibold">
          {label}
        </div>
        <button
          data-testid={`md-panel-${id}-close`}
          onClick={onClose}
          className="h-6 w-6 grid place-items-center rounded-md text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] hover:bg-white/5"
          title="Close panel"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto pl-noscroll p-3">
        {renderPanel(id, onAddElement)}
      </div>
    </aside>
  );
}

function renderPanel(id, onAdd) {
  if (id === "templates")   return <TemplatesPanel />;
  if (id === "elements")    return <ElementsPanel onAdd={onAdd} />;
  if (id === "text")        return <TextStylesPanel onAdd={onAdd} />;
  if (id === "image")       return <ImageStylesPanel />;
  if (id === "brand")       return <BrandKitPanel />;
  if (id === "uploads")     return <UploadsPanel />;
  if (id === "ai")          return <AIPanel />;
  if (id === "layers")      return <SmartLayersPlaceholder />;
  return null;
}

/* ---------- Templates ---------- */
function TemplatesPanel() {
  const items = [
    { label: "Flyer · 8.5×11", grad: "linear-gradient(135deg,#27E6D1,#0b6358)" },
    { label: "Banner · 1500×500", grad: "linear-gradient(135deg,#8b3dff,#27E6D1)" },
    { label: "Slide · 16:9", grad: "linear-gradient(135deg,#B9FF2F,#0b6358)" },
    { label: "Social Ad 1:1", grad: "linear-gradient(135deg,#ff4e84,#ff9c36)" },
    { label: "Story 9:16", grad: "linear-gradient(135deg,#27E6D1,#8b3dff)" },
    { label: "Thumbnail 16:9", grad: "linear-gradient(135deg,#0c1f1c,#27E6D1)" },
  ];
  return (
    <div className="space-y-3">
      <input
        placeholder="Search templates…"
        className="w-full h-9 rounded-lg px-3 text-[12px] outline-none"
        style={{ background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)", color: "var(--pl-text)" }}
      />
      <div className="grid grid-cols-2 gap-2">
        {items.map((t) => (
          <div
            key={t.label}
            className="rounded-lg overflow-hidden border border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/60 cursor-pointer"
            data-testid={`md-tpl-${t.label.split(" ")[0].toLowerCase()}`}
          >
            <div className="aspect-[4/3]" style={{ background: t.grad }} />
            <div className="px-2 py-1.5 text-[10.5px] text-[var(--pl-text)]">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Elements ---------- */
function ElementsPanel({ onAdd }) {
  const cats = ["Shapes", "Graphics", "Photos", "Videos", "3D", "Forms", "Animations", "Audio", "Charts", "Frames", "Mockups"];
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {cats.map((c) => (
          <button
            key={c}
            className="h-7 px-2.5 rounded-full text-[10.5px] border border-[var(--pl-border)] text-[var(--pl-text-muted)] hover:text-[var(--pl-aqua)] hover:border-[var(--pl-aqua)]/40"
          >
            {c}
          </button>
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] pt-1">Shapes</div>
      <div className="grid grid-cols-3 gap-2">
        {["Square","Circle","Rounded","Pill","Triangle","Diamond","Hex","Star","Arrow"].map((sh) => (
          <button
            key={sh}
            data-testid={`md-shape-${sh.toLowerCase()}`}
            onClick={() =>
              onAdd({
                type: "shape",
                name: sh,
                style: {
                  left: 80, top: 80, width: 140, height: 80, borderRadius: sh === "Pill" ? 999 : sh === "Rounded" ? 18 : sh === "Circle" ? 999 : 4,
                  background: "rgba(39,230,209,0.18)", border: "1px solid rgba(39,230,209,0.45)",
                  display: "grid", placeItems: "center", color: "var(--pl-aqua)", fontWeight: 800, fontSize: 12,
                },
                text: sh,
              })
            }
            className="aspect-[4/3] rounded-lg border border-[var(--pl-border)] text-[var(--pl-text-muted)] text-[10px] grid place-items-center hover:border-[var(--pl-aqua)]/60 hover:text-[var(--pl-aqua)]"
          >
            {sh}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Text Styles ---------- */
function TextStylesPanel({ onAdd }) {
  const styles = [
    { name: "Headline",  size: 48, weight: 900, family: "Playfair Display, serif" },
    { name: "Subheading",size: 22, weight: 700 },
    { name: "Body",      size: 14, weight: 500 },
    { name: "Caption",   size: 11, weight: 600, family: "JetBrains Mono" },
  ];
  return (
    <div className="space-y-2">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Add text</div>
      {styles.map((s) => (
        <button
          key={s.name}
          data-testid={`md-text-${s.name.toLowerCase()}`}
          onClick={() =>
            onAdd({
              type: "text",
              name: s.name,
              style: { left: 100, top: 120, width: 360, color: "var(--pl-text)", fontSize: s.size, fontWeight: s.weight, fontFamily: s.family, lineHeight: 1.15 },
              text: s.name + " text",
            })
          }
          className="w-full text-left rounded-lg p-3 border border-[var(--pl-border)] hover:border-[var(--pl-aqua)]/60 bg-white/[0.03]"
          style={{ fontFamily: s.family, fontSize: Math.min(s.size, 18), fontWeight: s.weight, color: "var(--pl-text)" }}
        >
          {s.name}
        </button>
      ))}
    </div>
  );
}

/* ---------- Image Styles ---------- */
function ImageStylesPanel() {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Curated stock</div>
      <div className="grid grid-cols-2 gap-2">
        {[1,2,3,4,5,6].map((i) => (
          <div key={i} className="aspect-square rounded-lg border border-[var(--pl-border)]" style={{
            background: `radial-gradient(circle at ${30+i*8}% ${20+i*6}%, rgba(39,230,209,0.4), transparent 40%), linear-gradient(135deg,#0c1f1c,#27E6D1)`,
          }} />
        ))}
      </div>
    </div>
  );
}

/* ---------- Brand Kit ---------- */
function BrandKitPanel() {
  const colors = ["#27E6D1","#B9FF2F","#8b3dff","#ff4e84","#ff9c36","#001713","#f4fffc","#0c1f1c"];
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Brand colors</div>
      <div className="grid grid-cols-4 gap-2">
        {colors.map((c) => (
          <button key={c} className="h-9 rounded-lg border border-[var(--pl-border)]" style={{ background: c }} title={c} />
        ))}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] pt-2">Fonts</div>
      <div className="space-y-1.5">
        {["Playfair Display","Inter","JetBrains Mono","Manrope"].map((f) => (
          <div key={f} className="rounded-lg p-2.5 border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text)] text-[13px]" style={{ fontFamily: f }}>{f}</div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Uploads ---------- */
function UploadsPanel() {
  return (
    <div className="space-y-3">
      <button className="w-full h-9 rounded-lg text-[12px] font-bold" style={{
        color: "#001b18",
        background: "linear-gradient(135deg,#27E6D1,#B9FF2F)",
      }}>Upload media</button>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)]">Recent uploads</div>
      <div className="text-[12px] text-[var(--pl-text-muted)] p-3 rounded-lg border border-dashed border-[var(--pl-border)] text-center">
        Drop files here or click upload
      </div>
    </div>
  );
}

/* ---------- AI ---------- */
function AIPanel() {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Generate with AI</div>
      <textarea
        placeholder="Describe a banner, flyer, or campaign creative…"
        className="w-full h-24 rounded-lg p-2.5 text-[12px] outline-none resize-none"
        style={{ background: "var(--pl-bg-2)", border: "1px solid var(--pl-border)", color: "var(--pl-text)" }}
      />
      <button className="w-full h-9 rounded-lg text-[12px] font-bold inline-flex items-center justify-center gap-2" style={{
        color: "#001b18", background: "linear-gradient(135deg,#27E6D1,#B9FF2F)",
      }}>
        <Sparkles className="h-3.5 w-3.5" /> Generate
      </button>
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-text-muted)] pt-2">Quick prompts</div>
      <div className="space-y-1.5">
        {["Bold ad for fintech webinar", "Holiday sale social post", "Coach launch hero banner"].map((p) => (
          <button key={p} className="w-full text-left rounded-lg p-2.5 border border-[var(--pl-border)] text-[12px] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/60">
            {p}
          </button>
        ))}
      </div>
    </div>
  );
}

/* ---------- Smart Layers placeholder ---------- */
function SmartLayersPlaceholder() {
  return (
    <div className="space-y-3">
      <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)]">Smart Layers · preview</div>
      <div className="text-[12px] text-[var(--pl-text-muted)] p-3 rounded-lg border border-[var(--pl-border)] bg-white/[0.03]">
        Full Smart Layers (rename, eye, lock, drag reorder, groups) is coming in the next turn.
      </div>
    </div>
  );
}
